import {
  CA_SUBDIVISIONS,
  type CountryCode,
  countryOfRegion,
  regionCode,
  regionFor,
  US_SUBDIVISIONS,
} from '@app/shared'

/**
 * Where an address is, as far as the region rule reads it: its country, and
 * the state or province it names. Both are strings because a form's draft
 * holds strings; a blank country is a person who has not said, which only a
 * person's own place allows.
 */
export type Place = { country: string; region: string }

/** The country an address is in until somebody says otherwise. */
export const DEFAULT_COUNTRY = 'US'

/** What the second picker is called, and what it offers, in a country that
 *  names a region from a list. */
export type RegionList = {
  label: 'State' | 'Province'
  placeholder: string
  options: { value: string; label: string }[]
}

const toOptions = (list: readonly { code: string; name: string }[]) =>
  list.map((r) => ({ value: r.code, label: r.name }))

const LISTS: Record<string, RegionList> = {
  US: {
    label: 'State',
    placeholder: 'Choose a state',
    options: toOptions(US_SUBDIVISIONS),
  },
  CA: {
    label: 'Province',
    placeholder: 'Choose a province',
    options: toOptions(CA_SUBDIVISIONS),
  },
}

/** The list a country's addresses pick their region from; null where an
 *  address names none, and so shows no region field at all. */
export const regionList = (country: string): RegionList | null =>
  LISTS[country] ?? null

/** A country picked: a region belongs to the country it was picked in, so
 *  another country starts with none — unless no country was said before and
 *  the region held is one of the new country's own. */
export const withCountry = (place: Place, country: string): Place => ({
  country,
  region:
    country === place.country ||
    (!place.country && countryOfRegion(place.region) === country)
      ? place.region
      : '',
})

/** The region as held when it is not one the country's list names — a state
 *  that is not a code on the list, or any state on an address outside the US
 *  and Canada. Null when it is blank or on the list. */
export const offListRegion = (place: Place): string | null => {
  const raw = place.region.trim()
  if (!raw) return null
  if (!place.country) return regionCode(raw) === null ? raw : null
  const read = regionFor(place.country, raw)
  return 'refused' in read || read.region !== raw ? raw : null
}

/** What a field says about a region off the list, as the thing to do. Null
 *  where there is nothing to pick — a state on an address outside the US and
 *  Canada, which `strayRegion` answers instead. */
export const offListProblem = (place: Place): string | null => {
  const raw = offListRegion(place)
  if (raw === null) return null
  const list = regionList(place.country)
  if (list) {
    return `“${raw}” is not on the list — pick the ${list.label.toLowerCase()}.`
  }
  return place.country
    ? null
    : `“${raw}” is not on the list — pick the country, then the state.`
}

/** A state held on an address whose country names none; null otherwise. */
export const strayRegion = (place: Place): string | null =>
  place.country && !regionList(place.country) ? offListRegion(place) : null

/**
 * A place as a form opens it for editing: a stray state is already cleared,
 * so the form opens with that change pending and saving it is one press.
 * Compared against the place as held, the clearing is what the save sends.
 */
export const openPlace = <P extends Place>(place: P): P =>
  strayRegion(place) === null ? place : { ...place, region: '' }

/** What the second line of an address is called in this country. */
export const postalLabel = (country: string | null | undefined): string =>
  country === DEFAULT_COUNTRY ? 'ZIP code' : 'Postal code'

/**
 * The country and region a save sends, given the place it started from.
 *
 * Nothing when neither moved, so a save that changed a phone or a name never
 * carries a region it did not touch — and an untouched one off the list never
 * refuses it. When the place moved both go together, because a country alone
 * leaves its state in question. A region off the list is never sent: it goes
 * as none.
 */
export function placeToSend(
  initial: Place,
  current: Place,
): { country: CountryCode; region: string | null } | Record<string, never> {
  /* A blank country is a person who has not said, and nothing on the form
     can make one blank — so it is always the place they started from. */
  if (!current.country) return {}
  if (
    current.country === initial.country &&
    current.region === initial.region
  ) {
    return {}
  }
  const country = current.country as CountryCode
  const read = regionFor(country, current.region)
  return { country, region: 'refused' in read ? null : read.region }
}

const naming =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(undefined, { type: 'region' })
    : null

/** A country by its reader's own name for it; the code where the runtime
 *  cannot name it. */
export const countryName = (code: string): string => {
  try {
    return naming?.of(code) ?? code
  } catch {
    return code
  }
}

/** The country an address is in, named, where it is not the US — which
 *  every address is assumed to be in unless it says otherwise. '' for the US
 *  and for an address that names none. */
export const foreignCountry = (code: string | null | undefined): string =>
  code && code !== DEFAULT_COUNTRY ? countryName(code) : ''

type Address = {
  addressLine?: string | null
  city?: string | null
  region?: string | null
  postalCode?: string | null
  country?: string | null
}

const joined = (parts: (string | null | undefined)[], by: string) =>
  parts
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(by)

/**
 * An address as the lines an envelope sets it on: the street, then the town,
 * region and postal code, with the country only when it is not the US — which
 * every address is assumed to be in unless it says otherwise. Blank lines are
 * left out.
 */
export function placeLines(a: Address): string[] {
  return [joined([a.addressLine], ''), townLine(a)].filter(Boolean)
}

/** The line after the street: town, region and postal code, and the country
 *  where it is not the US. */
export const townLine = (a: Address): string =>
  joined(
    [a.city, joined([a.region, a.postalCode], ' '), foreignCountry(a.country)],
    ', ',
  )

/** The same address as one line; '' for an address with no parts. */
export const placeLine = (a: Address): string => placeLines(a).join(', ')
